import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RelatedServicesNav from '../components/RelatedServicesNav';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  Syringe, Shield, ClipboardCheck, HeartPulse, Baby, Dog, Cat, Rabbit, Sparkles,
  Bug, Activity, Calendar, ClipboardList, BookOpen, HandHeart, ShieldCheck,
  MessageSquare, MapPin, Phone, MessageCircle, CalendarDays, ArrowRight, Users,
  Microscope, Clock, ThermometerSun, AlertCircle, CheckCircle2, Stethoscope,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE = 'https://plus.unsplash.com/premium_photo-1664300914147-6da4f19c9a74?auto=format&fit=crop&w=1400&q=80';

const TITLE = 'Pet Vaccination in Noida | Dog & Cat Vaccination Clinic | Ichi & Ori Pet Care';
const DESCRIPTION = 'Protect your pet with timely vaccinations at Ichi & Ori Pet Care, Noida. We provide personalised vaccination schedules for puppies, kittens, adult dogs and cats in a safe, compassionate environment.';
const CANONICAL = 'https://ichiandoripetcare.com/pet-vaccination-noida';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const WHY_VACCINES_MATTER = [
  { Icon: Shield,       title: 'How vaccines work',              text: 'Vaccines gently expose the immune system to a safe version of a virus or bacteria, so the body learns to recognise and respond to it long before your pet ever meets the real thing.' },
  { Icon: HeartPulse,   title: 'Prevention over treatment',      text: 'Treating parvovirus, distemper or panleukopenia is difficult, expensive and sometimes unsuccessful. A single, timely vaccine avoids that entire journey.' },
  { Icon: Users,        title: 'Community protection',           text: 'When most pets in a neighbourhood are vaccinated, disease has fewer places to travel. Your pet’s protection quietly helps every other family, too.' },
  { Icon: AlertCircle,  title: 'Life-threatening disease risk',  text: 'Rabies, leptospirosis and canine distemper can be fatal and, in the case of rabies, transmissible to humans. Vaccination is the simplest safeguard against them.' },
  { Icon: Calendar,     title: 'Timing changes everything',      text: 'Vaccines work best when given at the right age, with the right gaps and the right boosters. Staying on schedule is often more important than the vaccine choice itself.' },
  { Icon: ClipboardCheck, title: 'Health checks along the way',  text: 'Every vaccination visit is also a mini wellness check. Weight, coat, ears, teeth and behaviour are all reviewed while you are already at the clinic.' },
];

const DOG_VACCINES = [
  { title: 'DHPPi-L',                subtitle: 'Core combination vaccine', text: 'Protects against distemper, hepatitis, parvovirus, parainfluenza and leptospirosis in one shot, the foundation of every dog’s vaccination plan.' },
  { title: 'Anti-Rabies',            subtitle: 'Core',                     text: 'Legally recommended in India and essential everywhere. Rabies is preventable through vaccination and fatal without it.' },
  { title: 'Leptospirosis',          subtitle: 'Core in NCR',              text: 'Often included in DHPPi-L, and boosted separately when needed. Especially relevant during and after monsoon in Noida.' },
  { title: 'Bordetella (Kennel Cough)', subtitle: 'Lifestyle',             text: 'Highly recommended for dogs who visit boarding, grooming, dog parks or other multi-dog environments.' },
  { title: 'Canine Coronavirus',     subtitle: 'Lifestyle',                text: 'Considered alongside puppy vaccination in high-exposure environments and where local risk is elevated.' },
  { title: 'Canine Influenza',       subtitle: 'Where indicated',          text: 'Reserved for dogs with meaningful exposure risk, such as regular boarding, day-care or travel to high-density facilities.' },
  { title: 'Lyme Disease',           subtitle: 'Where indicated',          text: 'Considered for dogs in known tick-heavy environments or those travelling to endemic regions.' },
];

const CAT_VACCINES = [
  { title: 'FVRCP',                  subtitle: 'Core combination vaccine', text: 'Protects against feline viral rhinotracheitis, calicivirus and panleukopenia, three of the most serious viral threats to cats.' },
  { title: 'Anti-Rabies',            subtitle: 'Core',                     text: 'Recommended for every cat, including indoor cats, and required for travel and boarding in many facilities.' },
  { title: 'FeLV (Feline Leukemia)', subtitle: 'Where indicated',          text: 'Discussed for kittens and for cats with any outdoor access, based on lifestyle and exposure history.' },
];

const RABBIT_VACCINES = [
  { title: 'Vaccination guidance',   subtitle: 'Case by case',             text: 'Vaccine availability for pet rabbits in India is limited and varies by region. We provide honest, current guidance during consultation based on what is safely accessible.' },
];

const SCHEDULE_NOTES = [
  { Icon: Baby,           title: 'First vaccinations',    text: 'Puppies typically start their core vaccination series between 6 and 8 weeks. Kittens usually begin around 6 to 8 weeks as well, with schedules adjusted for weight, health and mother-derived immunity.' },
  { Icon: Syringe,        title: 'Booster doses',         text: 'A young pet’s immune system needs repeated exposure to build reliable protection. This is why a puppy or kitten usually needs multiple visits over a few weeks, not just one shot.' },
  { Icon: Calendar,       title: 'Annual boosters',       text: 'Adult dogs and cats generally return each year for a booster review. Timing and vaccine choice depend on age, lifestyle and previous history rather than a rigid calendar.' },
  { Icon: AlertCircle,    title: 'Missed vaccinations',   text: 'Missing a booster does not always mean starting over. Depending on the gap and the vaccine, we can often resume the schedule safely without doubling back on doses.' },
  { Icon: ClipboardList,  title: 'Why schedules differ',  text: 'Two puppies of the same breed may follow different plans, based on age at first visit, prior vaccines, deworming status and household risk. Every schedule is built for the individual pet.' },
  { Icon: MessageSquare,  title: 'Talk before you plan',  text: 'These are general principles, not medical advice. Please share your pet’s history with the veterinarian so we can build a schedule that is safe, effective and right for your family.' },
];

const BEFORE_AFTER = [
  {
    Icon: Clock,
    tag: 'BEFORE THE VISIT',
    title: 'Preparing your pet',
    text: 'Bring previous vaccination records, deworming details and any recent medications. A calm morning, a light meal and a short walk or play session help your pet arrive relaxed and ready.',
    bullets: ['Carry old records or reports', 'Note recent symptoms, if any', 'Feed a light meal, not a large one', 'Cat carriers with familiar bedding'],
  },
  {
    Icon: Stethoscope,
    tag: 'AT THE CLINIC',
    title: 'Health check first',
    text: 'Every pet is examined before any vaccine. If your pet is running a fever, actively unwell or heavily immune-compromised, the veterinarian may reschedule the vaccine and treat the underlying issue first.',
    bullets: ['Temperature and weight check', 'Coat, eyes, ears and gums review', 'Deworming status confirmed', 'Vaccine choice discussed with you'],
  },
  {
    Icon: ThermometerSun,
    tag: 'AFTER THE VACCINE',
    title: 'Common mild reactions',
    text: 'Some pets are a little quieter, slightly warm to the touch or a bit off food for 12 to 24 hours. Mild swelling at the injection site is also common and usually settles on its own.',
    bullets: ['Quiet, low-key rest for a day', 'Cool water and light meals', 'Avoid heavy exercise for 24 hours', 'Small injection-site swelling is normal'],
  },
  {
    Icon: AlertCircle,
    tag: 'WHEN TO CALL',
    title: 'Signs to reach out',
    text: 'Serious vaccine reactions are uncommon but need prompt attention. Please call or WhatsApp us if you notice any of the signs below, especially in the first few hours after a vaccination.',
    bullets: ['Facial swelling or hives', 'Repeated vomiting or diarrhea', 'Difficulty breathing', 'Collapse or extreme lethargy'],
  },
];

const EDU_ESSAY = [
  { q: 'Can vaccinated pets still get sick?',   a: 'Yes, but usually far less severely. Vaccines significantly reduce the risk of infection and, when infection does happen, tend to make the illness milder and shorter. No vaccine is 100 percent, which is why timing, boosters and general health also matter.' },
  { q: 'Are vaccines safe?',                    a: 'The vaccines we use are well studied and considered safe for most pets. Serious side effects are rare. Mild reactions like brief tiredness or a small local swelling are more common and pass on their own within a day or two.' },
  { q: 'Can an older pet be vaccinated?',       a: 'Absolutely. Adult pets, including seniors, can safely be vaccinated. Older pets sometimes benefit from adjusted schedules, and the veterinarian will consider overall health, existing conditions and lifestyle before recommending a plan.' },
  { q: 'Can indoor cats skip vaccines?',        a: 'Even fully indoor cats benefit from core vaccination. Viruses can be brought in on clothing and shoes, cats occasionally escape, and boarding, grooming or travel usually require up-to-date records.' },
  { q: 'What happens if I miss a booster?',     a: 'A missed booster usually just means an updated plan, not a full restart. The veterinarian will look at how long it has been, which vaccine was missed and your pet’s current health before deciding the safest next step.' },
  { q: 'Can multiple vaccines be given together?', a: 'Often yes. Many vaccines are safely combined in a single visit and even in a single injection. Whether to combine or space them out depends on age, health and how your pet has responded to vaccines in the past.' },
  { q: 'Why does my puppy need several visits?',   a: 'A puppy’s immune system is still learning. Antibodies from the mother interfere with vaccine response in early weeks, so a series of vaccines given a few weeks apart is needed to build lasting protection.' },
];

const WHY_ICHI = [
  'Gentle, low-stress handling',
  'Personalised vaccination plans',
  'Clear, jargon-free explanations',
  'Digital vaccination records',
  'Timely reminder messages',
  'Experienced veterinary guidance',
  'Calm, unhurried environment',
  'Honest advice on lifestyle vaccines',
];

const LOCATIONS = [
  'Sector 90', 'Sector 137', 'Sector 143', 'Sector 168', 'Sector 93', 'Sector 100',
  'Noida Expressway', 'Nearby residential communities',
];

const FAQS = [
  { q: 'Which vaccines does my puppy need?',              a: 'Most puppies begin with DHPPi-L (covering distemper, hepatitis, parvovirus, parainfluenza and leptospirosis), followed by Anti-Rabies and Kennel Cough at the appropriate time. Additional vaccines like Canine Coronavirus, Canine Influenza or Lyme may be considered based on lifestyle. The exact plan is finalised in consultation with the veterinarian.' },
  { q: 'Which vaccines does my kitten need?',             a: 'Kittens generally start with FVRCP between 6 and 8 weeks of age, followed by boosters and Anti-Rabies at the right point. FeLV is considered based on lifestyle and household. Each kitten\'s schedule is tailored to their history and health.' },
  { q: 'How often are booster vaccinations needed?',      a: 'Most adult dogs and cats return for a booster review once a year. Some vaccines have longer intervals depending on the product used and your pet\'s lifestyle. Your veterinarian will confirm the right timing at each visit.' },
  { q: 'Can my pet be vaccinated when sick?',             a: 'Usually no. If your pet has a fever, active infection, severe stress or a compromised immune system, the veterinarian will treat the underlying issue first and reschedule the vaccine for a healthier day.' },
  { q: 'How much time does a vaccination visit take?',    a: 'A typical vaccination visit takes around 20 to 30 minutes, which includes a physical exam, a short conversation about the plan, the vaccine itself and a few minutes of settled observation before you head home.' },
  { q: 'Do indoor cats really need vaccines?',            a: 'Yes. Core vaccines such as FVRCP and Anti-Rabies remain important even for indoor-only cats. Boarding facilities, groomers and travel often require up-to-date vaccination records, and rare escapes do happen.' },
  { q: 'Can adult pets start vaccinations for the first time?', a: 'Yes. Rescued adults, older adopted pets and pets with lost records can safely begin a vaccination plan at any age. The veterinarian will structure a catch-up schedule that respects the pet\'s current health.' },
  { q: 'When should I deworm before vaccination?',        a: 'Deworming is usually done a few days before a scheduled vaccine so the immune system is not distracted while responding to the vaccine. The exact timing depends on your pet\'s current status and is decided during consultation.' },
  { q: 'Do you send vaccination reminders?',              a: 'Yes. We keep a digital record of every vaccination and share a reminder message ahead of the next booster, so keeping your pet\'s protection current does not depend on memory alone.' },
  { q: 'How do I book a vaccination appointment?',        a: 'You can book directly through the Request Appointment button on this page, message us on WhatsApp at +91 9560925999 or call the clinic between 10:30 AM and 8:30 PM. A quick message with your pet\'s age and history is usually enough to reserve a slot.' },
  { q: 'Where is the clinic located?',                    a: 'Ichi & Ori Pet Care is at G11, Tower C, Noida World One, Sector 90, Noida, Uttar Pradesh 201305, easily reached from Noida Expressway and nearby residential communities in Sectors 90, 93, 100, 137, 143 and 168.' },
];

const INTERNAL_LINKS = [
  { to: '/veterinary-clinic-noida', label: 'Veterinary Clinic in Noida' },
  { to: '/dog-vet-noida',           label: 'Dog Vet in Noida' },
  { to: '/cat-vet-noida',           label: 'Cat Vet in Noida' },
  { to: '/pet-doctor-noida',        label: 'Pet Doctor in Noida' },
  { to: '/veterinary',              label: 'Puppy Vaccination' },
  { to: '/veterinary',              label: 'Kitten Vaccination' },
  { to: '/veterinary',              label: 'Pet Diagnostics in Noida' },
  { to: '/veterinary',              label: 'Pet Surgery in Noida' },
];

const PetVaccinationNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a vaccination appointment at Ichi & Ori Pet Care.")}`;

  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Pet Vaccination Clinic in Noida',
    url: CANONICAL, telephone: CONTACT.phoneRaw, priceRange: '₹₹',
    address: { '@type': 'PostalAddress', streetAddress: 'G11, Tower C, Noida World One', addressLocality: 'Sector 90, Noida', addressRegion: 'Uttar Pradesh', postalCode: '201305', addressCountry: 'IN' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(GOOGLE_RATING?.rating || 4.9), reviewCount: String(GOOGLE_RATING?.count || 107), bestRating: '5' },
    areaServed: { '@type': 'City', name: 'Noida' },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pet Vaccination in Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="pet-vaccination-noida-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_25%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_75%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6">
              <Syringe className="w-3.5 h-3.5 flex-shrink-0" />PET VACCINATION · NOIDA
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">Protect Your Pet with Timely Vaccinations.</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-9 max-w-xl">Vaccinations are one of the simplest, most effective ways to protect pets from serious infectious diseases. At Ichi &amp; Ori Pet Care, every vaccination plan is tailored to your pet&rsquo;s age, lifestyle and health status.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_vaccination_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-vaccination-hero-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_vaccination_hero')} className="w-full sm:w-auto">
                <Button data-testid="pet-vaccination-hero-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_vaccination_hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img
                src={HERO_IMAGE}
                alt="Veterinarian preparing a vaccine syringe next to a calm white dog — pet vaccination at Ichi and Ori Pet Care, Sector 90 Noida (placeholder image)"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <RelatedServicesNav current="/pet-vaccination-noida" />

      {/* 2 — WHY VACCINATIONS MATTER */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY VACCINATIONS MATTER</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A small shot. A lifetime of protection.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Most of the diseases that used to devastate puppies and kittens are now largely avoidable, and the reason is simple. A well-timed vaccine gives your pet&rsquo;s immune system a safe head start against threats it might otherwise meet unprepared.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_VACCINES_MATTER.map((r, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 hover:shadow-md transition-all flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><r.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2">{r.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — VACCINATIONS WE OFFER */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_20%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_80%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">VACCINATIONS WE OFFER</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08]">Core and lifestyle vaccines, thoughtfully chosen.</h2>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mt-5">We follow current veterinary guidance and adjust each plan around your pet&rsquo;s exposure risk and history. Below is an overview, not a prescription. The right combination is finalised in consultation.</p>
          </div>

          {/* Dogs */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center"><Dog className="w-5 h-5 text-[#ffc1e0]" /></div>
              <h3 className="text-2xl sm:text-3xl font-extrabold">For Dogs</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {DOG_VACCINES.map((v, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 hover:border-[#e870b6]/50 transition-all flex flex-col h-full">
                  <h4 className="font-bold text-white text-lg mb-1 leading-snug">{v.title}</h4>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#ffc1e0] mb-3">{v.subtitle.toUpperCase()}</span>
                  <p className="text-sm text-white/75 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cats */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center"><Cat className="w-5 h-5 text-[#ffc1e0]" /></div>
              <h3 className="text-2xl sm:text-3xl font-extrabold">For Cats</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CAT_VACCINES.map((v, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 hover:border-[#e870b6]/50 transition-all flex flex-col h-full">
                  <h4 className="font-bold text-white text-lg mb-1 leading-snug">{v.title}</h4>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#ffc1e0] mb-3">{v.subtitle.toUpperCase()}</span>
                  <p className="text-sm text-white/75 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Rabbits */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center"><Rabbit className="w-5 h-5 text-[#ffc1e0]" /></div>
              <h3 className="text-2xl sm:text-3xl font-extrabold">For Rabbits</h3>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {RABBIT_VACCINES.map((v, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 flex flex-col">
                  <h4 className="font-bold text-white text-lg mb-1 leading-snug">{v.title}</h4>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#ffc1e0] mb-3">{v.subtitle.toUpperCase()}</span>
                  <p className="text-sm text-white/75 leading-relaxed max-w-2xl">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4 — PUPPY & KITTEN VACCINATION SCHEDULES */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">VACCINATION SCHEDULES</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Puppy and kitten schedules, explained simply.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Every young pet grows into their own vaccination plan. These are general principles to help you feel prepared, not fixed medical advice. The final schedule is always built together in consultation.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SCHEDULE_NOTES.map((s, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-7 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2 leading-snug">{s.title}</h3>
                <p className="text-sm text-[#3b1d76]/85 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — BEFORE & AFTER VACCINATION */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">BEFORE &amp; AFTER</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What to expect around vaccination day.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">A little preparation helps most vaccinations go smoothly. Here is the calm, honest version of what a visit looks like and how to care for your pet afterwards.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BEFORE_AFTER.map((s, i) => (
              <div key={i} className="bg-white rounded-[24px] p-8 border border-[#f3d6f5]/60 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center flex-shrink-0"><s.Icon className="w-5 h-5 text-[#e870b6]" /></div>
                  <span className="text-[10px] tracking-[0.22em] font-bold text-[#5b3eb5]">{s.tag}</span>
                </div>
                <h3 className="font-bold text-[#1a0b3d] text-2xl mb-3 leading-snug">{s.title}</h3>
                <p className="text-[15px] text-[#3b1d76]/85 leading-relaxed mb-5">{s.text}</p>
                <ul className="space-y-2 mt-auto">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#1a0b3d] font-medium">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#e870b6] flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 — VACCINATION Q&A EDUCATIONAL */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">HELPFUL TO KNOW</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Common vaccination questions, answered honestly.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">A short educational read for pet parents who want to understand vaccines before their next visit.</p>
          </div>
          <div className="space-y-6">
            {EDU_ESSAY.map((f, i) => (
              <div key={i} className="bg-[#fdf7fb] rounded-2xl p-6 sm:p-7 border border-[#f3d6f5]/60">
                <h3 className="font-bold text-[#1a0b3d] text-lg mb-2 leading-snug">{f.q}</h3>
                <p className="text-[#3b1d76]/85 text-[15px] leading-[1.85]">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — WHY PET PARENTS CHOOSE US */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(240px,360px)_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="rounded-[24px] overflow-hidden shadow-xl border border-[#f5d3e6] bg-white">
              <img
                src={cdnImg(DR_PORTRAIT, { w: 720, h: 900, fit: 'cover', a: 'center' })}
                alt="Dr Kaushal Singh Yadav, veterinarian at Ichi and Ori Pet Care, Sector 90 Noida"
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY PET PARENTS CHOOSE US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08] mb-8">Vaccination care built around your pet.</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {WHY_ICHI.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5"><ClipboardCheck className="w-4 h-4 text-[#e870b6]" /></span>
                  <span className="text-[#1a0b3d] text-[15px] font-semibold">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/book" onClick={() => trackBookButtonClick('pet_vaccination_why')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1a0b3d] hover:bg-[#3b1d76] text-white text-sm font-semibold transition-colors">Book a Vaccination <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8 — SERVING NOIDA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">SERVING NOIDA</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A trusted vaccination clinic across South Noida.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Our clinic in Sector 90 is easy to reach from Noida Expressway and the neighbouring residential sectors, with quiet parking and short waits at appointment time.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {LOCATIONS.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fdf7fb] border border-[#f3d6f5] text-[#1a0b3d] text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#e870b6]" /> {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 9 — FAQ */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Vaccination questions, answered clearly.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6] bg-white rounded-2xl" data-testid="pet-vaccination-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-4">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 10 — RELATED SERVICES */}
      <section className="bg-white py-16 sm:py-20 border-y border-[#f3d6f5]/60">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">EXPLORE RELATED CARE</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a0b3d] leading-[1.1] mb-8">Related veterinary services in Noida.</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {INTERNAL_LINKS.map((l, i) => (
              <Link key={i} to={l.to} className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#fdf7fb] border border-[#f3d6f5] hover:border-[#e870b6] hover:shadow-md text-[#1a0b3d] text-sm font-semibold transition-all">
                {l.label} <ArrowRight className="w-4 h-4 text-[#e870b6] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — WHAT MAKES ICHI & ORI DIFFERENT */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">THE ICHI &amp; ORI DIFFERENCE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What makes Ichi &amp; Ori different.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">We believe great veterinary care is about more than diagnosing illness. It&apos;s about building trust, communicating honestly and treating every pet with the same compassion we&apos;d want for our own.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Users,          title: 'Treat Every Pet Like Family',   text: 'Every recommendation we make is the same one we would make if your pet were our own.' },
              { Icon: ClipboardCheck, title: 'Honest, Transparent Care',      text: 'We explain every diagnosis, treatment option and expected cost before moving forward.' },
              { Icon: Microscope,     title: 'Evidence-Based Decisions',      text: 'Modern diagnostics and careful clinical judgement help us recommend the right treatment, not unnecessary procedures.' },
              { Icon: HeartPulse,     title: 'Long-Term Relationships',       text: 'Whether it is a puppy’s first vaccination or care for a senior pet, we are here to support your family through every stage of your pet’s life.' },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0"><p.Icon className="w-5 h-5 text-[#5b3eb5]" /></div>
                <h3 className="font-serif text-[20px] font-bold text-[#1a0b3d] mb-3 leading-snug">{p.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">Protect Your Pet for Life, Starting Today.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">Keeping vaccinations up to date is one of the kindest, most consistent things you can do for your pet. Book a visit and let&rsquo;s build a schedule that fits your family, your lifestyle and your pet&rsquo;s stage of life.</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('pet_vaccination_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-vaccination-final-book-btn" className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('pet_vaccination_final')} className="w-full sm:w-auto">
                  <Button data-testid="pet-vaccination-final-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp</Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('pet_vaccination_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StickyMobileCta source="petvaccination" />
    </div>
  );
};

export default PetVaccinationNoida;
